import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Play, Pause, Volume2, VolumeX, Maximize, Minimize,
  ArrowLeft, Settings, SkipBack, SkipForward, Cast, Check,
  Bell, X, Mail, Sparkles, Eye, Share2, Copy, MessageCircle
} from 'lucide-react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Hls from 'hls.js';
import useEnhancedVideoAds from '../hooks/useEnhancedVideoAds';
import VODFooter from '../components/VODFooter';

const API = process.env.REACT_APP_BACKEND_URL || '';
const SITE_URL = typeof window !== 'undefined' ? window.location.origin : ''; // Dynamic URL for social sharing

// VAST tag URL - Using Google's SKIPPABLE test tag in HD (skip after 5 seconds)
// TODO: Replace with your own VAST tag once Google Ad Manager is properly configured
// VAST tag is now fetched dynamically from ad settings via useEnhancedVideoAds hook

const VODWatch = () => {
  const navigate = useNavigate();
  const { videoId } = useParams();
  const [searchParams] = useSearchParams();
  const isSharedLink = searchParams.get('shared') === 'true';
  const shouldAutoPlay = searchParams.get('autoplay') === 'true';
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const containerRef = useRef(null);
  const progressRef = useRef(null);
  const adContainerRef = useRef(null);
  
  const [video, setVideo] = useState(null);
  const [streamUrl, setStreamUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // R2 4K video state
  const [isR2Video, setIsR2Video] = useState(false);
  const [r2Url4k, setR2Url4k] = useState('');
  const [r2Url1080p, setR2Url1080p] = useState('');
  const [selectedQuality, setSelectedQuality] = useState('4k'); // '4k' or '1080p'
  
  // Auto quality switching state
  const [autoQualityEnabled, setAutoQualityEnabled] = useState(true);
  const [bufferingCount, setBufferingCount] = useState(0);
  const [qualityDropped, setQualityDropped] = useState(false);
  const [showQualityNotification, setShowQualityNotification] = useState(false);
  const bufferingTimeoutRef = useRef(null);
  const lastBufferCheckRef = useRef(Date.now());
  
  // Player state
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [buffered, setBuffered] = useState(0);
  
  // Quality selector state
  const [showQualityMenu, setShowQualityMenu] = useState(false);
  const [availableQualities, setAvailableQualities] = useState([]);
  const [currentQuality, setCurrentQuality] = useState('auto');
  
  // Casting state
  const [isCasting, setIsCasting] = useState(false);
  const [castAvailable, setCastAvailable] = useState(false);
  
  // Ad state
  const [showAdOverlay, setShowAdOverlay] = useState(false);
  
  // Share modal state
  const [showShareModal, setShowShareModal] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  
  // Mobile detection
  const [isMobile, setIsMobile] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);
  
  // Orientation change state preservation
  const playbackStateRef = useRef({
    wasPlaying: false,
    currentTime: 0,
    isMuted: false,
    volume: 1,
    isFullscreen: false
  });
  
  // Track if we're in the middle of an orientation change
  const isRotatingRef = useRef(false);
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Handle orientation change - preserve playback state seamlessly
  useEffect(() => {
    const savePlaybackState = () => {
      if (videoRef.current && !videoRef.current.paused) {
        playbackStateRef.current = {
          wasPlaying: !videoRef.current.paused,
          currentTime: videoRef.current.currentTime,
          isMuted: videoRef.current.muted,
          volume: videoRef.current.volume,
          isFullscreen: !!document.fullscreenElement
        };
        isRotatingRef.current = true;
        console.log('[VODWatch] Orientation changing, saving state:', playbackStateRef.current);
      }
    };
    
    const restorePlaybackState = () => {
      if (!isRotatingRef.current) return;
      
      if (videoRef.current && playbackStateRef.current.wasPlaying) {
        // Only seek if significant time difference (>1 second)
        const timeDiff = Math.abs(videoRef.current.currentTime - playbackStateRef.current.currentTime);
        if (timeDiff > 1) {
          videoRef.current.currentTime = playbackStateRef.current.currentTime;
        }
        
        videoRef.current.muted = playbackStateRef.current.isMuted;
        videoRef.current.volume = playbackStateRef.current.volume;
        
        // Resume playback immediately
        const playPromise = videoRef.current.play();
        if (playPromise !== undefined) {
          playPromise.catch(err => {
            console.log('[VODWatch] Resume after rotation failed, trying muted:', err);
            videoRef.current.muted = true;
            videoRef.current.play().catch(e => console.log('[VODWatch] Muted play also failed:', e));
          });
        }
        
        console.log('[VODWatch] Playback restored after orientation change');
      }
      
      isRotatingRef.current = false;
    };
    
    // Handle orientation change start
    const handleOrientationChangeStart = () => {
      savePlaybackState();
    };
    
    // Handle orientation change complete with minimal delay
    const handleOrientationChangeEnd = () => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          restorePlaybackState();
        });
      });
    };
    
    // Listen for orientation change
    window.addEventListener('orientationchange', handleOrientationChangeStart);
    
    // Handle resize for orientation changes
    let resizeTimeout;
    let lastWidth = window.innerWidth;
    
    const handleResize = () => {
      const currentWidth = window.innerWidth;
      if (Math.abs(currentWidth - lastWidth) > 100) {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(handleOrientationChangeEnd, 50);
        lastWidth = currentWidth;
      }
    };
    window.addEventListener('resize', handleResize);
    
    // Screen orientation API for modern browsers
    if (screen.orientation) {
      screen.orientation.addEventListener('change', handleOrientationChangeStart);
    }
    
    // Handle visibility change
    const handleVisibilityChange = () => {
      if (!document.hidden && isRotatingRef.current) {
        restorePlaybackState();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      window.removeEventListener('orientationchange', handleOrientationChangeStart);
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (screen.orientation) {
        screen.orientation.removeEventListener('change', handleOrientationChangeStart);
      }
      clearTimeout(resizeTimeout);
    };
  }, []);
  
  // Check if user has premium membership
  const customer = JSON.parse(localStorage.getItem('livestreamCustomer') || 'null');
  const [hasPremiumSubscription, setHasPremiumSubscription] = useState(false);
  
  // Fetch subscription status from API for more accurate premium check
  useEffect(() => {
    const fetchSubscriptionStatus = async () => {
      try {
        const token = localStorage.getItem('livestreamToken');
        if (!token) {
          setHasPremiumSubscription(false);
          return;
        }
        
        const res = await axios.get(`${API}/api/vod/membership/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Check if user has active premium subscription
        if (res.data.subscription && res.data.subscription.tier === 'premium' && res.data.subscription.status === 'active') {
          setHasPremiumSubscription(true);
          console.log('[VODWatch] User has premium subscription - ads disabled');
        } else {
          setHasPremiumSubscription(false);
        }
      } catch (err) {
        console.error('[VODWatch] Error fetching subscription status:', err);
        setHasPremiumSubscription(false);
      }
    };
    
    fetchSubscriptionStatus();
  }, []);
  
  // Check premium status from both localStorage (quick) and API (accurate)
  const userIsPremium = hasPremiumSubscription || customer?.is_premium || customer?.membership_tier === 'premium' || customer?.has_active_subscription;
  
  // Video Ads Hook - disable ads for premium users OR premium tier videos
  const shouldShowAds = video && video.tier !== 'premium' && !userIsPremium;
  
  const {
    adPlaying,
    adPaused,
    imaReady,
    adSettings,
    playPreRoll,
    skipCustomAd,
    showSkipButton,
    skipCountdown,
    customAd,
    adType,
    toggleAdPlayPause,
    resetAds
  } = useEnhancedVideoAds({
    videoRef: videoRef,
    adContainerRef: adContainerRef,
    enableAds: shouldShowAds,
    onAdStart: () => {
      console.log('[VODWatch] Ad started');
      setShowAdOverlay(true);
    },
    onAdEnd: () => {
      console.log('[VODWatch] Ad ended');
      setShowAdOverlay(false);
    },
    onAdError: (error) => {
      console.log('[VODWatch] Ad error:', error);
      setShowAdOverlay(false);
    }
  });
  
  // Debug: Log ad settings when loaded
  useEffect(() => {
    if (adSettings) {
      console.log('[VODWatch] Ad settings loaded:', {
        break1: adSettings.ad_break_1_enabled,
        break2: `${adSettings.ad_break_2_enabled} at ${adSettings.ad_break_2_time}s (${adSettings.ad_break_2_time/60}min)`,
        break3: `${adSettings.ad_break_3_enabled} at ${adSettings.ad_break_3_time}s`,
        rotation: adSettings.rotation_percentage
      });
    }
  }, [adSettings]);
  
  // Signup prompt state (for non-logged in users)
  const [showSignupPrompt, setShowSignupPrompt] = useState(false);
  const [signupPromptShown, setSignupPromptShown] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  
  // Check if user is logged in
  useEffect(() => {
    const token = localStorage.getItem('vodToken') || localStorage.getItem('livestreamToken');
    setIsLoggedIn(!!token);
    
    // Check if we've already shown the prompt in this session
    const promptShown = sessionStorage.getItem('vodSignupPromptShown');
    if (promptShown) {
      setSignupPromptShown(true);
    }
  }, []);
  
  // Track video watches for non-logged in users
  const trackVideoWatch = () => {
    if (isLoggedIn) return;
    
    const watchCount = parseInt(localStorage.getItem('vodWatchCount') || '0');
    const newCount = watchCount + 1;
    localStorage.setItem('vodWatchCount', newCount.toString());
    
    // Show prompt after 2 videos watched
    if (newCount >= 2 && !signupPromptShown) {
      setShowSignupPrompt(true);
      setSignupPromptShown(true);
      sessionStorage.setItem('vodSignupPromptShown', 'true');
    }
  };
  
  // Show signup prompt at 50% of video
  const checkShowSignupAt50Percent = (currentProgress) => {
    if (isLoggedIn || signupPromptShown) return;
    
    if (currentProgress >= 50 && currentProgress < 52) {
      setShowSignupPrompt(true);
      setSignupPromptShown(true);
      sessionStorage.setItem('vodSignupPromptShown', 'true');
    }
  };
  
  const controlsTimeoutRef = useRef(null);
  const watchStartTimeRef = useRef(null);
  const lastTrackTimeRef = useRef(0);
  const trackingIntervalRef = useRef(null);
  const hasTrackedInitialPlayRef = useRef(false);
  const resumePositionRef = useRef(0);
  const hasResumedRef = useRef(false);
  
  // Resume playback state
  const [showResumePrompt, setShowResumePrompt] = useState(false);
  const [savedProgress, setSavedProgress] = useState(0);

  // Check for Chromecast availability
  useEffect(() => {
    // Check for AirPlay support (Safari)
    if (videoRef.current?.webkitShowPlaybackTargetPicker) {
      setCastAvailable(true);
    }
    
    // Check for Chromecast (Chrome)
    if (window.chrome?.cast) {
      setCastAvailable(true);
    }
  }, []);

  // Get device info for tracking
  const getDeviceInfo = () => {
    const ua = navigator.userAgent;
    let device = 'desktop';
    if (/Mobile|Android|iPhone|iPad/i.test(ua)) {
      device = /iPad|Tablet/i.test(ua) ? 'tablet' : 'mobile';
    }
    
    let browser = 'Unknown';
    if (ua.includes('Chrome')) browser = 'Chrome';
    else if (ua.includes('Firefox')) browser = 'Firefox';
    else if (ua.includes('Safari')) browser = 'Safari';
    else if (ua.includes('Edge')) browser = 'Edge';
    
    return { device, browser };
  };

  // Track watch event
  const trackWatchEvent = async (watchDuration, totalDuration) => {
    try {
      const token = localStorage.getItem('livestreamToken');
      
      // Always save to localStorage for resume functionality (works for all users)
      if (videoId && watchDuration > 0 && totalDuration > 0) {
        const completionRate = Math.round((watchDuration / totalDuration) * 100);
        localStorage.setItem(`vod_progress_${videoId}`, JSON.stringify({
          progress: Math.round(watchDuration),
          duration: Math.round(totalDuration),
          completion_rate: completionRate,
          saved_at: new Date().toISOString()
        }));
      }
      
      // If logged in, also save to server
      if (token && videoId) {
        const { device, browser } = getDeviceInfo();
        
        await axios.post(
          `${API}/api/vod/track-watch`,
          {
            video_id: videoId,
            watch_duration: Math.round(watchDuration),
            total_duration: Math.round(totalDuration),
            referrer: document.referrer || null,
            device_type: device,
            browser: browser
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
    } catch (err) {
      console.error('Failed to track watch event:', err);
    }
  };

  useEffect(() => {
    fetchVideoStream();
    
    // Track watch start time
    watchStartTimeRef.current = Date.now();
    let actualWatchedSeconds = 0;
    let lastTickTime = Date.now();
    
    // Set up periodic tracking every 30 seconds — track ACTUAL elapsed watch time, not video position
    trackingIntervalRef.current = setInterval(() => {
      if (videoRef.current && !videoRef.current.paused) {
        const now = Date.now();
        const elapsed = (now - lastTickTime) / 1000;
        if (elapsed > 0 && elapsed <= 35) {
          actualWatchedSeconds += elapsed;
        }
        lastTickTime = now;
        
        const totalDuration = videoRef.current.duration;
        if (actualWatchedSeconds > 5 && totalDuration > 0) {
          trackWatchEvent(actualWatchedSeconds, totalDuration);
        }
      } else {
        lastTickTime = Date.now();
      }
    }, 30000);
    
    return () => {
      if (trackingIntervalRef.current) {
        clearInterval(trackingIntervalRef.current);
      }
      
      if (hlsRef.current) {
        hlsRef.current.destroy();
      }
      
      // Track final watch time on unmount
      if (videoRef.current && actualWatchedSeconds > 5) {
        const totalDuration = videoRef.current.duration || video?.duration || 0;
        trackWatchEvent(actualWatchedSeconds, totalDuration);
      }
    };
  }, [videoId]);

  useEffect(() => {
    if (streamUrl && videoRef.current) {
      initializePlayer();
    }
  }, [streamUrl]);

  // Re-initialize for R2 videos when available
  useEffect(() => {
    if (isR2Video && (r2Url4k || r2Url1080p) && videoRef.current) {
      initializePlayer();
    }
  }, [isR2Video, r2Url4k, r2Url1080p]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      const isFs = !!(document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement);
      setIsFullscreen(isFs);
      // Show controls when entering/exiting fullscreen
      setShowControls(true);
    };
    
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
    };
  }, []);

  const fetchVideoStream = async () => {
    try {
      const token = localStorage.getItem('livestreamToken') || localStorage.getItem('adminToken');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      // Add shared parameter if this is a shared link
      const sharedParam = isSharedLink ? '?shared=true' : '';
      const res = await axios.get(
        `${API}/api/vod/videos/${videoId}/watch${sharedParam}`,
        { headers }
      );

      setVideo(res.data.video);
      setStreamUrl(res.data.stream_url);
      
      // Check if this is an R2 video with 4K support
      const videoData = res.data.video;
      if (videoData.video_source === 'r2' && (videoData.r2_url_4k || videoData.r2_url_1080p)) {
        setIsR2Video(true);
        setR2Url4k(videoData.r2_url_4k || '');
        setR2Url1080p(videoData.r2_url_1080p || '');
        // Default to 4K if available, otherwise 1080p
        setSelectedQuality(videoData.r2_url_4k ? '4k' : '1080p');
      }
      
      // Check for saved progress
      let foundProgress = false;
      
      // First, try to get progress from server if logged in
      if (token) {
        try {
          const progressRes = await axios.get(
            `${API}/api/vod/watch-progress/${videoId}`,
            { headers }
          );
          if (progressRes.data.progress > 10 && progressRes.data.completion_rate < 95) {
            setSavedProgress(progressRes.data.progress);
            resumePositionRef.current = progressRes.data.progress;
            setShowResumePrompt(true);
            foundProgress = true;
          }
        } catch (progressErr) {
          // No saved progress on server - will check localStorage
          console.log('No server progress found, checking localStorage');
        }
      }
      
      // If no server progress, check localStorage (works for all users including anonymous)
      if (!foundProgress) {
        const localProgress = localStorage.getItem(`vod_progress_${videoId}`);
        if (localProgress) {
          try {
            const progressData = JSON.parse(localProgress);
            if (progressData.progress > 10 && progressData.completion_rate < 95) {
              setSavedProgress(progressData.progress);
              resumePositionRef.current = progressData.progress;
              setShowResumePrompt(true);
            }
          } catch (parseErr) {
            console.log('Error parsing local progress');
          }
        }
      }
    } catch (err) {
      console.error('Error fetching video stream:', err);
      if (err.response?.status === 403) {
        setError('You need to purchase this video or subscribe to watch');
      } else if (err.response?.status === 401) {
        // Redirect to login for non-free videos
        navigate(`/live/login?from=vod&redirect=/vod/watch/${videoId}`);
      } else {
        setError('Failed to load video');
      }
    } finally {
      setLoading(false);
    }
  };

  const initializePlayer = () => {
    const videoEl = videoRef.current;
    if (!videoEl) {
      console.error('[VODWatch] No video element found');
      return;
    }

    console.log('[VODWatch] Initializing player:', { 
      isR2Video, 
      r2Url4k: r2Url4k?.substring(0, 50), 
      r2Url1080p: r2Url1080p?.substring(0, 50),
      streamUrl: streamUrl?.substring(0, 50),
      selectedQuality 
    });

    // Clean up existing HLS instance
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    // For R2 videos, use direct MP4 playback
    if (isR2Video) {
      const videoUrl = selectedQuality === '4k' ? r2Url4k : r2Url1080p;
      console.log('[VODWatch] R2 video URL:', videoUrl);
      if (videoUrl) {
        videoEl.src = videoUrl;
        videoEl.load(); // Explicitly load
        videoEl.play().catch((err) => {
          console.log('[VODWatch] R2 autoplay prevented:', err.message);
        });
        
        // Set available qualities for R2 videos
        const qualities = [];
        if (r2Url4k) {
          qualities.push({ index: 0, label: '4K', quality: '4k', height: 2160 });
        }
        if (r2Url1080p) {
          qualities.push({ index: 1, label: '1080p', quality: '1080p', height: 1080 });
        }
        setAvailableQualities(qualities);
        setCurrentQuality(selectedQuality);
      } else {
        console.error('[VODWatch] R2 video marked but no URL available');
      }
      return;
    }
    
    // For Cloudflare Stream videos, use HLS
    if (!streamUrl) {
      console.error('[VODWatch] No stream URL available');
      return;
    }

    console.log('[VODWatch] Setting up HLS for:', streamUrl);

    if (Hls.isSupported()) {
      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: false,
        xhrSetup: function(xhr) {
          xhr.withCredentials = false;
        }
      });
      
      hlsRef.current = hls;
      hls.loadSource(streamUrl);
      hls.attachMedia(videoEl);
      
      hls.on(Hls.Events.MANIFEST_PARSED, (event, data) => {
        console.log('[VODWatch] HLS manifest parsed, starting playback');
        videoEl.play().catch((err) => {
          console.log('[VODWatch] HLS autoplay prevented:', err.message);
        });
        
        // Extract available quality levels
        const levels = hls.levels;
        if (levels && levels.length > 0) {
          const qualities = levels.map((level, index) => ({
            index,
            height: level.height,
            width: level.width,
            bitrate: level.bitrate,
            label: level.height ? `${level.height}p` : `${Math.round(level.bitrate / 1000)}kbps`
          }));
          
          // Sort by height descending
          qualities.sort((a, b) => (b.height || 0) - (a.height || 0));
          
          // Add auto option
          setAvailableQualities([
            { index: -1, label: 'Auto', isAuto: true },
            ...qualities
          ]);
        }
      });
      
      hls.on(Hls.Events.ERROR, (event, data) => {
        console.error('[VODWatch] HLS error:', data.type, data.details, data);
        if (data.fatal) {
          // Try fallback: use videodelivery.net domain
          if (streamUrl.includes('cloudflarestream.com')) {
            const videoId = streamUrl.split('/')[3];
            const fallbackUrl = `https://videodelivery.net/${videoId}/manifest/video.m3u8`;
            console.log('[VODWatch] Trying fallback URL:', fallbackUrl);
            hls.loadSource(fallbackUrl);
          } else {
            console.error('[VODWatch] Fatal HLS error, no fallback available');
            setError('Video playback error');
          }
        }
      });
    } else if (videoEl.canPlayType('application/vnd.apple.mpegurl')) {
      // Safari native HLS support
      videoEl.src = streamUrl;
      videoEl.play().catch(() => {});
      
      // Safari doesn't expose quality levels the same way
      setAvailableQualities([
        { index: -1, label: 'Auto', isAuto: true }
      ]);
    }
  };

  // Change video quality
  const changeQuality = (quality) => {
    // For R2 videos, switch between 4K and 1080p
    if (isR2Video) {
      if (quality.quality) {
        setSelectedQuality(quality.quality);
        setCurrentQuality(quality.quality);
        
        // Save current time and switch source
        const currentVideoTime = videoRef.current?.currentTime || 0;
        const newUrl = quality.quality === '4k' ? r2Url4k : r2Url1080p;
        
        if (newUrl && videoRef.current) {
          videoRef.current.src = newUrl;
          videoRef.current.currentTime = currentVideoTime;
          videoRef.current.play().catch(() => {});
        }
      }
      setShowQualityMenu(false);
      return;
    }
    
    // For HLS videos (Cloudflare Stream)
    if (hlsRef.current) {
      if (quality.index === -1) {
        // Auto mode
        hlsRef.current.currentLevel = -1;
        setCurrentQuality('auto');
      } else {
        hlsRef.current.currentLevel = quality.index;
        const level = hlsRef.current.levels[quality.index];
        setCurrentQuality(level?.height ? `${level.height}p` : 'manual');
      }
    }
    setShowQualityMenu(false);
  };

  // Auto quality drop when buffering detected
  const handleAutoQualityDrop = () => {
    if (!isR2Video || !r2Url1080p || selectedQuality !== '4k') return;
    
    const currentVideoTime = videoRef.current?.currentTime || 0;
    
    // Switch to 1080p
    setSelectedQuality('1080p');
    setCurrentQuality('1080p');
    setQualityDropped(true);
    setBufferingCount(0);
    
    // Show notification
    setShowQualityNotification(true);
    setTimeout(() => setShowQualityNotification(false), 5000);
    
    // Switch video source
    if (videoRef.current) {
      videoRef.current.src = r2Url1080p;
      videoRef.current.currentTime = currentVideoTime;
      videoRef.current.play().catch(() => {});
    }
  };

  // Handle Cast to TV
  const handleCast = () => {
    if (videoRef.current?.webkitShowPlaybackTargetPicker) {
      // Safari AirPlay
      videoRef.current.webkitShowPlaybackTargetPicker();
    } else if (window.chrome?.cast) {
      // Chrome Cast (would need full Cast SDK integration)
      alert('Cast is available. For full Chromecast support, please use the Chrome browser.');
    } else {
      alert('Casting is not available on this browser. Try Safari for AirPlay or Chrome for Chromecast.');
    }
  };

  // Handle resume playback
  const handleResume = () => {
    if (videoRef.current && resumePositionRef.current > 0) {
      videoRef.current.currentTime = resumePositionRef.current;
      hasResumedRef.current = true;
    }
    setShowResumePrompt(false);
  };

  // Handle start from beginning
  const handleStartOver = () => {
    hasResumedRef.current = true;
    // Clear localStorage progress when starting over
    localStorage.removeItem(`vod_progress_${videoId}`);
    setShowResumePrompt(false);
  };

  // Format time for resume prompt
  const formatResumeTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Track if pre-roll has been attempted
  const preRollAttemptedRef = useRef(false);
  
  const handlePlayPause = () => {
    const videoEl = videoRef.current;
    if (!videoEl) return;
    
    // Don't allow play/pause during ad
    if (adPlaying) return;
    
    if (videoEl.paused) {
      // Try to play pre-roll on first play (for standard tier videos)
      if (shouldShowAds && !preRollAttemptedRef.current && imaReady) {
        console.log('[VODWatch] Attempting pre-roll ad...');
        preRollAttemptedRef.current = true;
        const adStarted = playPreRoll();
        if (adStarted) {
          console.log('[VODWatch] Pre-roll ad started');
          return; // Ad will play, video will start after ad ends
        }
      }
      
      videoEl.play();
    } else {
      videoEl.pause();
      // Track when user pauses after watching significant time
      const watchDuration = videoEl.currentTime - lastTrackTimeRef.current;
      if (watchDuration > 30) { // Track every 30+ seconds of continuous watching
        trackWatchEvent(videoEl.currentTime, videoEl.duration);
        lastTrackTimeRef.current = videoEl.currentTime;
      }
    }
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (videoRef.current) {
      videoRef.current.volume = newVolume;
      setIsMuted(newVolume === 0);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(videoRef.current.muted);
    }
  };

  const handleSeek = (e) => {
    const video = videoRef.current;
    const progress = progressRef.current;
    if (!video || !progress) return;
    
    const rect = progress.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    video.currentTime = percent * video.duration;
  };

  const skip = (seconds) => {
    if (videoRef.current) {
      videoRef.current.currentTime += seconds;
    }
  };

  const toggleFullscreen = () => {
    // Check if already in fullscreen
    const isCurrentlyFullscreen = document.fullscreenElement || 
                                   document.webkitFullscreenElement || 
                                   document.mozFullScreenElement ||
                                   document.msFullscreenElement;
    
    if (isCurrentlyFullscreen) {
      // Exit fullscreen
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
      return;
    }
    
    // Determine which element to fullscreen
    let targetElement = null;
    
    if (adPlaying) {
      // Ad is playing - fullscreen the ad container or ad video
      const adContainer = adContainerRef.current;
      if (adContainer) {
        // Try to find the ad video element inside the container
        const adVideo = adContainer.querySelector('video');
        if (adVideo) {
          targetElement = adVideo;
        } else {
          targetElement = adContainer;
        }
      }
    }
    
    // Fallback to main container or video if no ad or ad element not found
    if (!targetElement) {
      targetElement = videoRef.current || containerRef.current;
    }
    
    if (!targetElement) return;
    
    // Enter fullscreen
    if (targetElement.webkitEnterFullscreen) {
      // iOS Safari - use native video fullscreen (only works on video elements)
      targetElement.webkitEnterFullscreen();
    } else if (targetElement.requestFullscreen) {
      targetElement.requestFullscreen();
    } else if (targetElement.webkitRequestFullscreen) {
      targetElement.webkitRequestFullscreen();
    } else if (targetElement.mozRequestFullScreen) {
      targetElement.mozRequestFullScreen();
    } else if (targetElement.msRequestFullscreen) {
      targetElement.msRequestFullscreen();
    } else {
      // Fallback to container
      if (containerRef.current?.requestFullscreen) {
        containerRef.current.requestFullscreen();
      }
    }
  };

  const handleMouseMove = () => {
    setShowControls(true);
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    controlsTimeoutRef.current = setTimeout(() => {
      if (isPlaying && !showQualityMenu) setShowControls(false);
    }, 3000);
  };
  
  // Also show controls on touch for mobile
  const handleTouchStart = () => {
    handleMouseMove();
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Format duration for display (e.g., "29 min")
  const formatDuration = (seconds) => {
    if (!seconds) return '';
    const mins = Math.floor(seconds / 60);
    const hrs = Math.floor(mins / 60);
    if (hrs > 0) return `${hrs}h ${mins % 60}m`;
    return `${mins} min`;
  };

  // Format view count (e.g., "1.2K views")
  const formatViews = (count) => {
    if (!count) return '0';
    if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
    if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
    return count.toString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center text-white">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">{error}</h2>
          <div className="flex gap-4 justify-center">
            <Button onClick={() => navigate(`/vod/video/${videoId}`)} className="bg-purple-600">
              View Video Details
            </Button>
            <Button onClick={() => navigate('/vod/membership')} variant="outline" className="border-purple-500 text-purple-400">
              View Subscriptions
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Mobile Layout - Fullscreen Video Player (same as desktop)
  if (isMobile) {
    return (
      <div 
        ref={containerRef}
        className="fixed inset-0 bg-black text-white"
        onMouseMove={handleMouseMove}
        onClick={!adPlaying ? handlePlayPause : undefined}
      >
        {/* Video fills entire screen */}
        <div className="absolute inset-0 flex items-center justify-center">
          {/* Ad Container - overlays video during ads */}
          <div 
            ref={adContainerRef} 
            className={`absolute inset-0 z-20 ${adPlaying ? 'pointer-events-auto' : 'pointer-events-none hidden'}`}
          />
          
          {/* Main Video */}
          <video
            ref={videoRef}
            className="w-full h-full object-contain"
            playsInline
            webkit-playsinline="true"
            x5-playsinline="true"
            autoPlay={false}
            controls={false}
            controlsList="nodownload noplaybackrate"
            disablePictureInPicture
            poster={video?.thumbnail_url}
            onContextMenu={(e) => e.preventDefault()}
            onError={(e) => {
              console.error('[VODWatch Mobile] Video error:', e.target.error);
              console.log('[VODWatch Mobile] Video src:', videoRef.current?.src);
            }}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onTimeUpdate={(e) => setCurrentTime(e.target.currentTime)}
            onDurationChange={(e) => setDuration(e.target.duration)}
            onProgress={(e) => {
              if (e.target.buffered.length > 0) {
                setBuffered(e.target.buffered.end(e.target.buffered.length - 1));
              }
            }}
          />
        </div>

        {/* Ad Controls Overlay */}
        {adPlaying && (
          <>
            {/* Top Bar - Advertisement badge and controls */}
            <div className="absolute top-0 left-0 right-0 z-[200] p-4 flex items-center justify-between pointer-events-auto bg-gradient-to-b from-black/60 to-transparent">
              <div className="bg-yellow-500 text-black px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-2">
                <span className="w-2 h-2 bg-black rounded-full animate-pulse"></span>
                Advertisement
              </div>
              <div className="flex items-center gap-2">
                <button 
                  onClick={(e) => { e.stopPropagation(); toggleAdPlayPause(); }}
                  className="bg-black/60 backdrop-blur-sm text-white p-2 rounded-full"
                >
                  {adPaused ? <Play className="h-5 w-5" /> : <Pause className="h-5 w-5" />}
                </button>
                <button 
                  onClick={(e) => { e.stopPropagation(); toggleFullscreen(); }}
                  className="bg-black/60 backdrop-blur-sm text-white p-2 rounded-full"
                >
                  <Maximize className="h-5 w-5" />
                </button>
              </div>
            </div>
            
            {/* Skip Ad Button */}
            {showSkipButton && (
              <button
                onClick={(e) => { e.stopPropagation(); skipCustomAd(); }}
                className="absolute bottom-20 right-4 z-[200] bg-white/90 text-black px-4 py-2 rounded font-semibold pointer-events-auto"
              >
                Skip Ad →
              </button>
            )}
            {!showSkipButton && skipCountdown > 0 && (
              <div className="absolute bottom-20 right-4 z-[200] bg-black/70 text-white px-4 py-2 rounded">
                Skip in {skipCountdown}s
              </div>
            )}
            
            {/* Bottom Control Bar for Ads */}
            <div className="absolute bottom-0 left-0 right-0 z-[200] bg-gradient-to-t from-black/90 to-transparent p-4 pointer-events-auto">
              <div className="flex items-center gap-4">
                <button 
                  onClick={(e) => { e.stopPropagation(); toggleAdPlayPause(); }}
                  className="text-white"
                >
                  {adPaused ? <Play className="h-6 w-6" /> : <Pause className="h-6 w-6" />}
                </button>
                <button 
                  onClick={(e) => { e.stopPropagation(); setIsMuted(!isMuted); }}
                  className="text-white"
                >
                  {isMuted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
                </button>
                <span className="text-yellow-400 text-sm font-medium">Ad Playing</span>
                <div className="flex-1" />
                <button 
                  onClick={(e) => { e.stopPropagation(); toggleFullscreen(); }}
                  className="text-white"
                >
                  <Maximize className="h-5 w-5" />
                </button>
              </div>
            </div>
          </>
        )}

        {/* Normal Video Controls (when not playing ad) */}
        {!adPlaying && (
          <>
            {/* Header */}
            <header 
              className={`absolute top-0 left-0 right-0 z-50 p-4 bg-gradient-to-b from-black/80 to-transparent transition-opacity duration-300 ${
                showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'
              }`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-4">
                <button
                  onClick={() => navigate(-1)}
                  className="text-white hover:bg-white/20 p-2 rounded-lg"
                >
                  <ArrowLeft className="h-5 w-5" />
                </button>
                <div className="flex-1 min-w-0">
                  <h1 className="font-semibold text-white/90 truncate text-sm">{video?.title}</h1>
                </div>
              </div>
            </header>

            {/* Center Play Button */}
            {!isPlaying && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-30">
                <div className="w-20 h-20 rounded-full bg-purple-600/80 flex items-center justify-center">
                  <Play className="h-10 w-10 text-white ml-1" fill="white" />
                </div>
              </div>
            )}

            {/* Bottom Controls */}
            <div 
              className={`absolute bottom-0 left-0 right-0 z-[250] bg-gradient-to-t from-black/90 to-transparent p-4 transition-opacity duration-300 ${
                showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'
              }`}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Progress Bar */}
              <div 
                ref={progressRef}
                className="w-full h-2 bg-white/20 rounded-full mb-4 cursor-pointer"
                onClick={(e) => { e.stopPropagation(); handleSeek(e); }}
              >
                <div 
                  className="h-full bg-purple-500 rounded-full relative"
                  style={{ width: `${(currentTime / duration) * 100}%` }}
                >
                  <div className="absolute right-0 top-1/2 -translate-y-1/2 w-4 h-4 bg-white rounded-full shadow-lg" />
                </div>
              </div>

              {/* Controls Row */}
              <div className="flex items-center gap-4">
                <button onClick={(e) => { e.stopPropagation(); handlePlayPause(); }} className="text-white">
                  {isPlaying ? <Pause className="h-6 w-6" /> : <Play className="h-6 w-6" />}
                </button>
                <button onClick={(e) => { e.stopPropagation(); skip(-10); }} className="text-white">
                  <SkipBack className="h-5 w-5" />
                </button>
                <button onClick={(e) => { e.stopPropagation(); skip(10); }} className="text-white">
                  <SkipForward className="h-5 w-5" />
                </button>
                <button onClick={(e) => { e.stopPropagation(); setIsMuted(!isMuted); }} className="text-white">
                  {isMuted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
                </button>
                <span className="text-sm text-white/80">
                  {formatTime(currentTime)} / {formatTime(duration)}
                </span>
                <div className="flex-1" />
                <button 
                  onClick={(e) => { e.stopPropagation(); setShowShareModal(true); }} 
                  className="text-white"
                >
                  <Share2 className="h-5 w-5" />
                </button>
                <button onClick={(e) => { e.stopPropagation(); toggleFullscreen(); }} className="text-white">
                  <Maximize className="h-5 w-5" />
                </button>
              </div>
            </div>
          </>
        )}

        {/* Share Modal */}
        {showShareModal && (
          <div 
            className="fixed inset-0 z-[300] flex items-center justify-center bg-black/80" 
            onClick={() => setShowShareModal(false)}
          >
            <div 
              className="bg-gray-900 rounded-2xl p-6 w-full max-w-sm mx-4 border border-white/10" 
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-white">Share</h3>
                <button onClick={() => setShowShareModal(false)} className="text-gray-400">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="flex gap-4 justify-center">
                <button
                  onClick={() => {
                    const url = `${SITE_URL}/api/vod/share/${video?.slug || videoId}`;
                    const text = `Check out "${video?.title}" on LTV VOD!`;
                    window.open(`https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`, '_blank');
                  }}
                  className="flex flex-col items-center gap-2 p-3"
                >
                  <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                    <svg viewBox="0 0 24 24" className="w-6 h-6 text-white fill-current">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                  </div>
                  <span className="text-xs text-gray-300">WhatsApp</span>
                </button>
                <button
                  onClick={() => {
                    const url = `${SITE_URL}/api/vod/share/${video?.slug || videoId}`;
                    navigator.clipboard.writeText(url);
                    setLinkCopied(true);
                    setTimeout(() => setLinkCopied(false), 2000);
                  }}
                  className="flex flex-col items-center gap-2 p-3"
                >
                  <div className="w-12 h-12 bg-gray-600 rounded-full flex items-center justify-center">
                    {linkCopied ? <Check className="w-6 h-6 text-green-400" /> : <Copy className="w-6 h-6 text-white" />}
                  </div>
                  <span className="text-xs text-gray-300">{linkCopied ? 'Copied!' : 'Copy Link'}</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Desktop Layout - Original fullscreen player
  return (
    <div 
      ref={containerRef}
      className="fixed inset-0 bg-black text-white"
      onMouseMove={handleMouseMove}
      onTouchStart={handleTouchStart}
    >
      {/* Header - Only show when controls visible */}
      <header 
        className={`absolute top-0 left-0 right-0 z-[9999] p-4 bg-gradient-to-b from-black/80 to-transparent transition-opacity duration-300 ${
          showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onMouseMove={(e) => {
          e.stopPropagation();
          handleMouseMove();
        }}
      >
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/vod/video/${videoId}`)}
            className="text-white hover:bg-white/20"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="font-semibold text-white/90 truncate">{video?.title}</h1>
          </div>
        </div>
      </header>

      {/* Video Container - True Fullscreen */}
      <div className="absolute inset-0 flex items-center justify-center bg-black" onClick={!adPlaying ? handlePlayPause : undefined}>
        {/* Ad Container - overlays video during ads */}
        <div 
          ref={adContainerRef} 
          className={`absolute inset-0 z-20 ${adPlaying ? 'pointer-events-auto' : 'pointer-events-none hidden'}`}
        />
        
        {/* Ad Playing Indicator */}
        {adPlaying && (
          <div className="absolute top-4 left-4 right-4 z-[200] flex items-center justify-between pointer-events-auto">
            <div className="bg-yellow-500 text-black px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-2">
              <span className="w-2 h-2 bg-black rounded-full animate-pulse"></span>
              Advertisement
            </div>
            {/* Ad Controls - Play/Pause and Fullscreen */}
            <div className="flex items-center gap-2">
              {/* Play/Pause Button for ads */}
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  toggleAdPlayPause();
                }}
                className="bg-black/60 backdrop-blur-sm text-white hover:bg-black/80 p-2 rounded-full transition-colors cursor-pointer"
                title={adPaused ? "Resume Ad" : "Pause Ad"}
              >
                {adPaused ? <Play className="h-5 w-5" /> : <Pause className="h-5 w-5" />}
              </button>
              {/* Fullscreen button */}
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  toggleFullscreen();
                }}
                className="bg-black/60 backdrop-blur-sm text-white hover:bg-black/80 p-2 rounded-full transition-colors cursor-pointer"
                title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
              >
                {isFullscreen ? <Minimize className="h-5 w-5" /> : <Maximize className="h-5 w-5" />}
              </button>
            </div>
          </div>
        )}
        
        {/* Skip Ad Button - shows after countdown */}
        {adPlaying && showSkipButton && (
          <div className="absolute bottom-20 right-4 z-[200] pointer-events-auto">
            <button
              onClick={(e) => {
                e.stopPropagation();
                skipCustomAd();
              }}
              className="bg-white/90 text-black px-4 py-2 rounded font-semibold hover:bg-white transition-colors cursor-pointer"
            >
              Skip Ad →
            </button>
          </div>
        )}
        
        {/* Skip Ad Countdown */}
        {adPlaying && !showSkipButton && skipCountdown > 0 && (
          <div className="absolute bottom-20 right-4 z-[200]">
            <div className="bg-black/70 text-white px-4 py-2 rounded font-medium">
              Skip in {skipCountdown}s
            </div>
          </div>
        )}
        
        {/* Bottom Control Bar for Ads */}
        {adPlaying && (
          <div className="absolute bottom-0 left-0 right-0 z-[200] bg-gradient-to-t from-black/90 to-transparent p-4 pointer-events-auto">
            <div className="flex items-center gap-4">
              <button 
                onClick={(e) => { e.stopPropagation(); toggleAdPlayPause(); }}
                className="text-white hover:text-purple-400 transition-colors"
              >
                {adPaused ? <Play className="h-6 w-6" /> : <Pause className="h-6 w-6" />}
              </button>
              <button 
                onClick={(e) => { e.stopPropagation(); setIsMuted(!isMuted); }}
                className="text-white hover:text-purple-400 transition-colors"
              >
                {isMuted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
              </button>
              <span className="text-yellow-400 text-sm font-medium">Ad Playing</span>
              <div className="flex-1" />
              <button 
                onClick={(e) => { e.stopPropagation(); toggleFullscreen(); }}
                className="text-white hover:text-purple-400 transition-colors"
              >
                {isFullscreen ? <Minimize className="h-5 w-5" /> : <Maximize className="h-5 w-5" />}
              </button>
            </div>
          </div>
        )}
        
        <video
          ref={videoRef}
          className="w-full h-full object-contain"
          playsInline
          webkit-playsinline="true"
          x5-playsinline="true"
          controls={false}
          controlsList="nodownload noplaybackrate"
          disablePictureInPicture
          poster={video?.thumbnail_url}
          onContextMenu={(e) => e.preventDefault()}
          onError={(e) => {
            console.error('[VODWatch] Video error:', e.target.error);
            console.log('[VODWatch] Video src:', videoRef.current?.src);
            console.log('[VODWatch] Stream URL:', streamUrl);
            console.log('[VODWatch] R2 URLs:', { r2Url4k, r2Url1080p, isR2Video });
          }}
          onPlay={() => {
            setIsPlaying(true);
            // Track initial play to ensure watch history is recorded
            if (!hasTrackedInitialPlayRef.current && videoRef.current) {
              const totalDuration = videoRef.current.duration || 0;
              if (totalDuration > 0) {
                hasTrackedInitialPlayRef.current = true;
                trackWatchEvent(1, totalDuration); // Track 1 second to mark as started
                trackVideoWatch(); // Track for signup prompt
              }
            }
          }}
          onPause={() => setIsPlaying(false)}
          onWaiting={() => {
            // Video is buffering - track for auto quality switch
            if (isR2Video && autoQualityEnabled && selectedQuality === '4k' && r2Url1080p && !qualityDropped) {
              setBufferingCount(prev => {
                const newCount = prev + 1;
                // If buffering happens 3+ times in quick succession, switch to 1080p
                if (newCount >= 3) {
                  console.log('Auto-switching to 1080p due to buffering');
                  handleAutoQualityDrop();
                }
                return newCount;
              });
              
              // Reset buffering count after 30 seconds of smooth playback
              if (bufferingTimeoutRef.current) {
                clearTimeout(bufferingTimeoutRef.current);
              }
              bufferingTimeoutRef.current = setTimeout(() => {
                setBufferingCount(0);
              }, 30000);
            }
          }}
          onTimeUpdate={(e) => {
            setCurrentTime(e.target.currentTime);
            // Fallback: track after 5 seconds of playback if initial tracking failed
            if (!hasTrackedInitialPlayRef.current && e.target.currentTime >= 5 && e.target.duration > 0) {
              hasTrackedInitialPlayRef.current = true;
              trackWatchEvent(e.target.currentTime, e.target.duration);
            }
            // Check if we should show signup prompt at 50%
            if (e.target.duration > 0) {
              const progress = (e.target.currentTime / e.target.duration) * 100;
              checkShowSignupAt50Percent(progress);
            }
          }}
          onDurationChange={(e) => {
            setDuration(e.target.duration);
            // Also try to track here in case onPlay fired before duration was available
            if (!hasTrackedInitialPlayRef.current && videoRef.current && !videoRef.current.paused) {
              hasTrackedInitialPlayRef.current = true;
              trackWatchEvent(1, e.target.duration);
            }
          }}
          onProgress={(e) => {
            if (e.target.buffered.length > 0) {
              setBuffered(e.target.buffered.end(e.target.buffered.length - 1));
            }
          }}
          onEnded={() => {
            // Track completion when video ends
            const videoEl = videoRef.current;
            if (videoEl) {
              trackWatchEvent(videoEl.duration, videoEl.duration);
              // Clear localStorage progress since video is complete
              localStorage.removeItem(`vod_progress_${videoId}`);
            }
          }}
        />

        {/* Resume Playback Prompt */}
        {showResumePrompt && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/70 z-50">
            <div className="bg-gray-900 rounded-2xl p-6 max-w-sm mx-4 text-center border border-white/10">
              <h3 className="text-xl font-bold mb-2">Resume Watching?</h3>
              <p className="text-gray-400 mb-4">
                You left off at {formatResumeTime(savedProgress)}
              </p>
              <div className="flex gap-3 justify-center">
                <Button
                  onClick={handleResume}
                  className="bg-purple-600 hover:bg-purple-500"
                  data-testid="resume-button"
                >
                  <Play className="h-4 w-4 mr-2" />
                  Resume
                </Button>
                <Button
                  onClick={handleStartOver}
                  variant="outline"
                  className="border-white/20 hover:bg-white/10"
                  data-testid="start-over-button"
                >
                  Start Over
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Auto Quality Drop Notification */}
        {showQualityNotification && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 z-50 animate-in fade-in slide-in-from-top duration-300">
            <div className="bg-black/90 backdrop-blur border border-amber-500/30 rounded-lg px-4 py-2 flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center">
                <Settings className="h-4 w-4 text-amber-400" />
              </div>
              <div>
                <p className="text-white text-sm font-medium">Quality adjusted to 1080p</p>
                <p className="text-gray-400 text-xs">Optimized for your connection speed</p>
              </div>
            </div>
          </div>
        )}

        {/* Signup Prompt Modal */}
        {showSignupPrompt && !isLoggedIn && (
          <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
            <div className="relative bg-gradient-to-br from-gray-900 to-black rounded-2xl p-8 max-w-md mx-4 border border-purple-500/30 shadow-2xl">
              {/* Close button */}
              <button
                onClick={() => setShowSignupPrompt(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
              
              {/* Icon */}
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                  <Bell className="h-8 w-8 text-white" />
                </div>
              </div>
              
              {/* Content */}
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-white mb-3">
                  Enjoying the content?
                </h3>
                <p className="text-gray-400">
                  Create a <span className="text-green-400 font-semibold">FREE account</span> to stay updated with:
                </p>
              </div>
              
              {/* Benefits */}
              <div className="space-y-3 mb-8">
                <div className="flex items-center gap-3 text-gray-300">
                  <div className="w-6 h-6 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="h-3 w-3 text-purple-400" />
                  </div>
                  <span>New episode releases</span>
                </div>
                <div className="flex items-center gap-3 text-gray-300">
                  <div className="w-6 h-6 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="h-3 w-3 text-purple-400" />
                  </div>
                  <span>Exclusive series & documentaries</span>
                </div>
                <div className="flex items-center gap-3 text-gray-300">
                  <div className="w-6 h-6 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="h-3 w-3 text-purple-400" />
                  </div>
                  <span>Live concerts & shows</span>
                </div>
                <div className="flex items-center gap-3 text-gray-300">
                  <div className="w-6 h-6 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="h-3 w-3 text-purple-400" />
                  </div>
                  <span>Save your watch progress</span>
                </div>
              </div>
              
              {/* CTA Buttons */}
              <div className="space-y-3">
                <Button
                  onClick={() => navigate('/live/login?from=vod&redirect=' + encodeURIComponent(window.location.pathname))}
                  className="w-full py-5 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-semibold"
                >
                  Create Free Account
                </Button>
                <button
                  onClick={() => setShowSignupPrompt(false)}
                  className="w-full text-gray-400 hover:text-white text-sm transition-colors py-2"
                >
                  Maybe later
                </button>
              </div>
              
              {/* Note */}
              <p className="text-center text-gray-500 text-xs mt-4">
                No credit card required. Free forever.
              </p>
            </div>
          </div>
        )}

        {/* Center Play/Pause Button - hide during ads */}
        {!isPlaying && !showResumePrompt && !adPlaying && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-20 h-20 rounded-full bg-purple-600/80 flex items-center justify-center">
              <Play className="h-10 w-10 text-white ml-1" fill="white" />
            </div>
          </div>
        )}
      </div>

      {/* Controls - Hide during ads (ad has its own control bar) */}
      {!adPlaying && (
      <div 
        className={`absolute bottom-0 left-0 right-0 z-[9999] bg-gradient-to-t from-black/90 via-black/60 to-transparent p-4 pb-6 transition-opacity duration-300 ${
          showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={(e) => e.stopPropagation()}
        onMouseMove={(e) => {
          e.stopPropagation();
          handleMouseMove();
        }}
      >
        {/* Progress Bar - disabled during ads */}
        <div 
          ref={progressRef}
          className={`w-full h-2 bg-white/20 rounded-full mb-4 group ${adPlaying ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
          onClick={(e) => !adPlaying && handleSeek(e)}
        >
          {/* Buffered */}
          <div 
            className="absolute h-2 bg-white/30 rounded-full"
            style={{ width: `${(buffered / duration) * 100}%` }}
          />
          {/* Progress */}
          <div 
            className="h-2 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full relative"
            style={{ width: `${(currentTime / duration) * 100}%` }}
          >
            <div className="absolute right-0 top-1/2 -translate-y-1/2 w-4 h-4 bg-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
        </div>

        {/* Controls Row */}
        <div className="flex items-center gap-4">
          {/* Play/Pause - works for both video and ads */}
          <button 
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              console.log('[VODWatch] Bottom play/pause clicked, adPlaying:', adPlaying);
              if (adPlaying) {
                // Use the ad toggle function from the hook
                toggleAdPlayPause();
              } else {
                // Normal video control
                handlePlayPause();
              }
            }} 
            className="text-white hover:text-purple-400 transition-colors cursor-pointer"
          >
            {adPlaying ? (
              adPaused ? <Play className="h-6 w-6" /> : <Pause className="h-6 w-6" />
            ) : (
              isPlaying ? <Pause className="h-6 w-6" /> : <Play className="h-6 w-6" />
            )}
          </button>

          {/* Skip Back - disabled during ads */}
          <button 
            onClick={(e) => {
              e.stopPropagation();
              if (!adPlaying) skip(-10);
            }} 
            className={`transition-colors ${adPlaying ? 'text-gray-600 cursor-not-allowed' : 'text-white hover:text-purple-400 cursor-pointer'}`}
            disabled={adPlaying}
          >
            <SkipBack className="h-5 w-5" />
          </button>

          {/* Skip Forward - disabled during ads */}
          <button 
            onClick={(e) => {
              e.stopPropagation();
              if (!adPlaying) skip(10);
            }} 
            className={`transition-colors ${adPlaying ? 'text-gray-600 cursor-not-allowed' : 'text-white hover:text-purple-400 cursor-pointer'}`}
            disabled={adPlaying}
          >
            <SkipForward className="h-5 w-5" />
          </button>

          {/* Volume */}
          <div className="flex items-center gap-2 group">
            <button onClick={toggleMute} className="text-white hover:text-purple-400 transition-colors">
              {isMuted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
            </button>
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={isMuted ? 0 : volume}
              onChange={handleVolumeChange}
              className="w-20 opacity-0 group-hover:opacity-100 transition-opacity accent-purple-500"
            />
          </div>

          {/* Time */}
          <span className="text-sm text-gray-400">
            {formatTime(currentTime)} / {formatTime(duration)}
          </span>

          {/* Spacer */}
          <div className="flex-1" />

          {/* Quality Selector */}
          {availableQualities.length > 0 && (
            <div className="relative">
              <button 
                onClick={() => setShowQualityMenu(!showQualityMenu)}
                className="text-white hover:text-purple-400 transition-colors flex items-center gap-1"
              >
                <Settings className="h-5 w-5" />
                <span className="text-xs hidden sm:inline">
                  {isR2Video ? (
                    <span className={`font-bold ${currentQuality === '4k' ? 'text-amber-400' : ''}`}>
                      {currentQuality === '4k' ? '4K' : '1080p'}
                      {autoQualityEnabled && !qualityDropped && ' (Auto)'}
                    </span>
                  ) : (
                    currentQuality === 'auto' ? 'Auto' : currentQuality
                  )}
                </span>
              </button>
              
              {/* Quality Menu */}
              {showQualityMenu && (
                <div className="absolute bottom-full right-0 mb-2 bg-black/95 rounded-lg border border-white/20 overflow-hidden min-w-[160px]">
                  {/* Auto option for R2 videos */}
                  {isR2Video && r2Url4k && r2Url1080p && (
                    <button
                      onClick={() => {
                        setAutoQualityEnabled(true);
                        setQualityDropped(false);
                        // Reset to 4K if currently on 1080p due to manual selection
                        if (selectedQuality === '1080p' && r2Url4k) {
                          const currentVideoTime = videoRef.current?.currentTime || 0;
                          setSelectedQuality('4k');
                          setCurrentQuality('4k');
                          if (videoRef.current) {
                            videoRef.current.src = r2Url4k;
                            videoRef.current.currentTime = currentVideoTime;
                            videoRef.current.play().catch(() => {});
                          }
                        }
                        setShowQualityMenu(false);
                      }}
                      className={`w-full px-4 py-2 text-left text-sm hover:bg-white/10 flex items-center justify-between border-b border-white/10 ${
                        autoQualityEnabled ? 'text-green-400' : 'text-white'
                      }`}
                    >
                      <span>Auto (Smart)</span>
                      {autoQualityEnabled && <Check className="h-4 w-4" />}
                    </button>
                  )}
                  
                  {availableQualities.map((quality, idx) => (
                    <button
                      key={idx}
                      onClick={() => {
                        // Disable auto mode when manually selecting quality
                        if (isR2Video) {
                          setAutoQualityEnabled(false);
                        }
                        changeQuality(quality);
                      }}
                      className={`w-full px-4 py-2 text-left text-sm hover:bg-white/10 flex items-center justify-between ${
                        !autoQualityEnabled && (
                          (quality.quality && currentQuality === quality.quality) ||
                          (!quality.isAuto && !quality.quality && currentQuality === quality.label)
                        )
                          ? quality.quality === '4k' ? 'text-amber-400' : 'text-purple-400'
                          : 'text-white'
                      }`}
                    >
                      <span className="flex items-center gap-2">
                        {quality.quality === '4k' && (
                          <span className="px-1.5 py-0.5 bg-amber-500 text-black text-xs font-bold rounded">4K</span>
                        )}
                        {quality.label}
                      </span>
                      {!autoQualityEnabled && (
                        (quality.quality && currentQuality === quality.quality) ||
                        (!quality.isAuto && !quality.quality && currentQuality === quality.label)
                      ) && (
                        <Check className="h-4 w-4" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Cast Button */}
          <button 
            onClick={handleCast}
            className={`transition-colors ${isCasting ? 'text-purple-400' : 'text-white hover:text-purple-400'}`}
            title="Cast to TV"
          >
            <Cast className="h-5 w-5" />
          </button>

          {/* Fullscreen */}
          <button 
            onClick={(e) => {
              e.stopPropagation();
              toggleFullscreen();
            }} 
            className="text-white hover:text-purple-400 transition-colors cursor-pointer"
          >
            {isFullscreen ? <Minimize className="h-5 w-5" /> : <Maximize className="h-5 w-5" />}
          </button>
        </div>

        {/* Video Info Bar - Modern Cinematic Style */}
        <div className="mt-4 pt-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              {/* Category Badge */}
              {video?.category?.name && (
                <div className="inline-flex items-center gap-2 mb-3">
                  <span className="px-3 py-1 bg-purple-500/20 border border-purple-500/30 rounded-full text-purple-300 text-xs font-medium uppercase tracking-wider">
                    {video.category.name}
                  </span>
                  {video?.has_4k && (
                    <span className="px-2 py-1 bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 rounded text-amber-300 text-xs font-bold">
                      4K
                    </span>
                  )}
                </div>
              )}
              
              {/* Title - Modern Typography */}
              <h2 className="text-2xl md:text-3xl font-bold text-white leading-tight tracking-tight mb-2">
                {video?.title}
              </h2>
              
              {/* Meta Info Row */}
              <div className="flex flex-wrap items-center gap-3 text-sm">
                {video?.duration > 0 && (
                  <span className="flex items-center gap-1.5 text-gray-400">
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/>
                      <polyline points="12 6 12 12 16 14"/>
                    </svg>
                    {formatDuration(video.duration)}
                  </span>
                )}
                <span className="flex items-center gap-1.5 text-gray-400">
                  <Eye className="h-4 w-4 text-purple-400" />
                  <span className="text-white/80">{formatViews(video?.view_count || 0)}</span>
                  <span className="text-gray-500">views</span>
                </span>
                {video?.release_date && (
                  <span className="text-gray-500">
                    {new Date(video.release_date).getFullYear()}
                  </span>
                )}
              </div>
            </div>
            
            {/* Share Button - Pill Style */}
            <button
              onClick={() => setShowShareModal(true)}
              className="flex items-center gap-2 px-5 py-2.5 bg-white/10 hover:bg-white/15 backdrop-blur-sm rounded-full text-white text-sm font-medium transition-all hover:scale-105 border border-white/10"
            >
              <Share2 className="h-4 w-4" />
              <span>Share</span>
            </button>
          </div>
        </div>
      </div>
      )}

      {/* Share Modal */}
      {showShareModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm" onClick={() => setShowShareModal(false)}>
          <div className="bg-gray-900 rounded-2xl p-6 w-full max-w-md mx-4 border border-white/10 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white">Share this video</h3>
              <button
                onClick={() => {
                  setShowShareModal(false);
                  setLinkCopied(false);
                }}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            {/* Video Preview */}
            <div className="flex items-center gap-3 bg-white/5 rounded-xl p-3 mb-6">
              <img 
                src={video?.thumbnail_url} 
                alt={video?.title}
                className="w-20 h-12 object-cover rounded-lg"
              />
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium text-sm truncate">{video?.title}</p>
                <p className="text-gray-400 text-xs">{video?.category?.name}</p>
              </div>
            </div>
            
            {/* Social Share Buttons */}
            <div className="grid grid-cols-4 gap-3 mb-6">
              {/* WhatsApp */}
              <button
                onClick={() => {
                  const url = `${SITE_URL}/vod/watch/${videoId}`;
                  const text = `Check out "${video?.title}" on LTV VOD!`;
                  window.open(`https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`, '_blank');
                }}
                className="flex flex-col items-center gap-2 p-3 bg-green-600/20 hover:bg-green-600/30 rounded-xl transition-colors group"
              >
                <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                  <svg viewBox="0 0 24 24" className="w-5 h-5 text-white fill-current">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                </div>
                <span className="text-xs text-gray-300">WhatsApp</span>
              </button>
              
              {/* Instagram */}
              <button
                onClick={() => {
                  const url = `${SITE_URL}/vod/watch/${videoId}`;
                  navigator.clipboard.writeText(url);
                  alert('Link copied! Open Instagram and paste in your Story or DM.');
                }}
                className="flex flex-col items-center gap-2 p-3 bg-pink-600/20 hover:bg-pink-600/30 rounded-xl transition-colors group"
              >
                <div className="w-10 h-10 bg-gradient-to-tr from-yellow-500 via-pink-500 to-purple-500 rounded-full flex items-center justify-center">
                  <svg viewBox="0 0 24 24" className="w-5 h-5 text-white fill-current">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </div>
                <span className="text-xs text-gray-300">Instagram</span>
              </button>
              
              {/* TikTok */}
              <button
                onClick={() => {
                  const url = `${SITE_URL}/vod/watch/${videoId}`;
                  navigator.clipboard.writeText(url);
                  alert('Link copied! Open TikTok and paste in your bio or message.');
                }}
                className="flex flex-col items-center gap-2 p-3 bg-white/10 hover:bg-white/20 rounded-xl transition-colors group"
              >
                <div className="w-10 h-10 bg-black rounded-full flex items-center justify-center border border-white/20">
                  <svg viewBox="0 0 24 24" className="w-5 h-5 text-white fill-current">
                    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
                  </svg>
                </div>
                <span className="text-xs text-gray-300">TikTok</span>
              </button>
              
              {/* Facebook */}
              <button
                onClick={() => {
                  const url = `${SITE_URL}/vod/watch/${videoId}`;
                  window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
                }}
                className="flex flex-col items-center gap-2 p-3 bg-blue-600/20 hover:bg-blue-600/30 rounded-xl transition-colors group"
              >
                <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
                  <svg viewBox="0 0 24 24" className="w-5 h-5 text-white fill-current">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </div>
                <span className="text-xs text-gray-300">Facebook</span>
              </button>
            </div>
            
            {/* Copy Link Section */}
            <div className="border-t border-white/10 pt-4">
              <p className="text-sm text-gray-400 mb-3">Or copy link</p>
              <div className="flex gap-2">
                <div className="flex-1 bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-sm text-gray-300 truncate">
                  {SITE_URL}/vod/watch/{videoId}
                </div>
                <Button
                  onClick={() => {
                    navigator.clipboard.writeText(`${SITE_URL}/vod/watch/${videoId}`);
                    setLinkCopied(true);
                    setTimeout(() => setLinkCopied(false), 2000);
                  }}
                  className={`px-4 ${linkCopied ? 'bg-green-600 hover:bg-green-600' : 'bg-purple-600 hover:bg-purple-500'}`}
                >
                  {linkCopied ? (
                    <>
                      <Check className="h-4 w-4 mr-2" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4 mr-2" />
                      Copy
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Footer */}
      <VODFooter />
    </div>
  );
};

export default VODWatch;
